/**
 * In-app debug log: ring buffer in memory, mirrored to localStorage so it
 * survives reload (and the WebGPU-process-restart that can happen if the
 * native shader compiler hits a fault). Surfaced via the Debug screen.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogEntry {
  ts: number;
  level: LogLevel;
  tag: string;
  message: string;
  detail?: string;
}

const MAX_ENTRIES = 500;
const STORAGE_KEY = "ideatriage.debugLog.v1";

let buffer: LogEntry[] = loadFromStorage();
const subscribers = new Set<() => void>();

function loadFromStorage(): LogEntry[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LogEntry[];
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-MAX_ENTRIES);
  } catch {
    return [];
  }
}

function persist(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  } catch {
    // quota exceeded — drop oldest half and retry
    buffer = buffer.slice(-Math.floor(MAX_ENTRIES / 2));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
    } catch {
      /* give up */
    }
  }
}

function append(entry: LogEntry): void {
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) {
    buffer = buffer.slice(-MAX_ENTRIES);
  }
  persist();
  // Mirror to console — cheap to read in DevTools too.
  const consoleFn =
    entry.level === "error"
      ? console.error
      : entry.level === "warn"
        ? console.warn
        : entry.level === "info"
          ? console.info
          : console.debug;
  consoleFn(`[${entry.tag}] ${entry.message}${entry.detail ? `\n${entry.detail}` : ""}`);
  for (const fn of subscribers) {
    try {
      fn();
    } catch {
      /* swallow */
    }
  }
}

export const DebugLog = {
  d(tag: string, message: string, detail?: string): void {
    append({ ts: Date.now(), level: "debug", tag, message, detail });
  },
  i(tag: string, message: string, detail?: string): void {
    append({ ts: Date.now(), level: "info", tag, message, detail });
  },
  w(tag: string, message: string, detail?: string): void {
    append({ ts: Date.now(), level: "warn", tag, message, detail });
  },
  e(tag: string, message: string, detail?: string | unknown): void {
    const detailStr =
      typeof detail === "string"
        ? detail
        : detail instanceof Error
          ? `${detail.name}: ${detail.message}\n${detail.stack ?? ""}`
          : detail !== undefined
            ? String(detail)
            : undefined;
    append({ ts: Date.now(), level: "error", tag, message, detail: detailStr });
  },
  snapshot(): LogEntry[] {
    return buffer.slice();
  },
  clear(): void {
    buffer = [];
    persist();
    for (const fn of subscribers) fn();
  },
  subscribe(fn: () => void): () => void {
    subscribers.add(fn);
    return () => {
      subscribers.delete(fn);
    };
  },
  toPlainText(): string {
    return buffer
      .map((e) => {
        const head = `${new Date(e.ts).toISOString()}  ${e.level.toUpperCase().padEnd(5)} ${e.tag}: ${e.message}`;
        return e.detail ? `${head}\n${e.detail}` : head;
      })
      .join("\n");
  },
};
